import { ICustomisationSettings } from "./customisation.interface";
import {
  PersonAttributes,
  AdditionalAttributes,
  AssetType,
  LoanPurpose,
  OpportunityAttributes,
  PropertyPurchaseAttributes,
  PropertyPurchaseFinancials,
  MenuAttributes,
  TextAttributes,
  ProductSearchResultAttributes,
  ProductType,
  ProductDetails,
  PaygIncomeAttributes
 } from '@shared/enums/customisation.enum';

export const customisation: ICustomisationSettings = {
  applicant: {
    personDetails: [
      PersonAttributes.Title,
      PersonAttributes.FirstName,
      PersonAttributes.MiddleName,
      PersonAttributes.LastName,
      PersonAttributes.PreferredName,
      PersonAttributes.Gender,
      PersonAttributes.DateOfBirth,
      PersonAttributes.RelationshipStatus
    ],
    furtherDetails: [
      AdditionalAttributes.BankingInstitution,
      AdditionalAttributes.RepayingStudentLoan,
      AdditionalAttributes.PermanentResident,
      AdditionalAttributes.AustralianCitizen
    ],

    shouldDisplayContactSearchModal: true
  },
  purposeType: {
    homeLoan: [
      LoanPurpose.Purchase,
      LoanPurpose.Refinance,
      LoanPurpose.RenovationConstruction,
      LoanPurpose.SwitchRefix,
      LoanPurpose.TitleBuyout,
      LoanPurpose.TopUp,
      LoanPurpose.Reverse,
      LoanPurpose.OtherPersonal,
      LoanPurpose.Business
    ],
    commercialLoan: [
      LoanPurpose.Purchase,
      LoanPurpose.Refinance,
      LoanPurpose.RenovationConstruction,
      LoanPurpose.SwitchRefix,
      LoanPurpose.TitleBuyout,
      LoanPurpose.TopUp,
      LoanPurpose.OtherPersonal,
      LoanPurpose.Business
    ],
    smsfLoan: [
      LoanPurpose.Purchase,
      LoanPurpose.Refinance
    ],
    motorLoan: [
      LoanPurpose.Consumer,
      LoanPurpose.Commercial
    ],
    personalLoan: [LoanPurpose.UnsecuredPersonal],
    insuranceLoan: [LoanPurpose.Insurance],
    refixLoan: [LoanPurpose.Refix]
  },
  summary: {
    opportunityDetail: [
      OpportunityAttributes.Name,
      OpportunityAttributes.EstimatedAmount,
      OpportunityAttributes.CreditAdviceFee,
      OpportunityAttributes.HowDidTheyHearAboutUs,
      OpportunityAttributes.CreatedDate,
      OpportunityAttributes.WorkflowStatus,
      OpportunityAttributes.ProviderReference,
      OpportunityAttributes.ReferrerArrangement,
      OpportunityAttributes.AssignedTo,
      OpportunityAttributes.KYC,
      OpportunityAttributes.FactFind,
      OpportunityAttributes.ModeSelector,
      OpportunityAttributes.AdviserCampaignId,
      OpportunityAttributes.Tags
    ],
    showSubmissionErrorMessage: false
  },
  purpose: {
    propertyPurchaseDetails: [
      PropertyPurchaseAttributes.PurchaseAmount,
      PropertyPurchaseAttributes.LoanAmount,
      PropertyPurchaseAttributes.PropertyType,
      PropertyPurchaseAttributes.Zoning,
      PropertyPurchaseAttributes.LandSize,
      PropertyPurchaseAttributes.TitleType,
      PropertyPurchaseAttributes.PurchaseStatus,
      PropertyPurchaseAttributes.Address,
      PropertyPurchaseAttributes.UseAfterSettlement,
      PropertyPurchaseAttributes.SettlementDate,
      PropertyPurchaseAttributes.FinanceDate,
      PropertyPurchaseAttributes.UseAsSecurity,
      PropertyPurchaseAttributes.NewBuilding,
      PropertyPurchaseAttributes.OTPDutiableLandValue
    ],
    propertyPurchaseFinancials: [
      PropertyPurchaseFinancials.GenuineSavings,
      PropertyPurchaseFinancials.DepositPaid,
      PropertyPurchaseFinancials.Gift,
      PropertyPurchaseFinancials.OngoingWeeklyRent,
      PropertyPurchaseFinancials.FirstHomeBuyer,
      PropertyPurchaseFinancials.FirstHomeBuyerGrant,
      PropertyPurchaseFinancials.StampDutyUpfront,
      PropertyPurchaseFinancials.LMIWaived
    ]
  },
  income: {
    paygIncomeDetails: [
      PaygIncomeAttributes.TaxExemptIncome
    ],
    excludePreviousYearByDefault: false
  },
  assets: {
    assetTypes: [
      AssetType.Property,
      AssetType.Vehicle,
      AssetType.Other,
      AssetType.Superannuation,
      AssetType.PropertySecurityGuarantee
    ]
  },
  menu: {
    sideMenu: {
      menuAttributes: [
        MenuAttributes.MenuHeader
      ],
      menuSections: [
        [
          {
            label: MenuAttributes.Summary,
            link: [],
            schemaPaths: ['referrerArrangement', 'adviceFee'],
          },
          {
            label: MenuAttributes.CustomerNeeds,
            link: ['Customer-Needs'],
            schemaPaths: [],
            violationSections: ['Questionnaire', 'Discussion Notes']
          },
          {
            // Story 3.0.1 + 3.2.1 (J1 carry-over) — Fact Find sub-nav.
            // Progress is the universal Fact Find landing. The Progress
            // component resolves the active Fact Find for the parent
            // Opportunity and either redirects to `:factFindId/progress`
            // or renders an empty state with a *Start Fact Find* CTA.
            label: MenuAttributes.FactFind,
            link: ['fact-find', 'progress'],
            schemaPaths: []
          },
          {
            label: MenuAttributes.Applicants,
            link: ['Applicants'],
            schemaPaths: [
              'applicants/individuals',
              'applicants/companies',
              'applicants/trusts',
              'dependents'
            ]
          },
          {
            label: MenuAttributes.Income,
            link: ['Income'],
            schemaPaths: ['incomes']
          },
          {
            label: MenuAttributes.Assets,
            link: ['Assets'],
            schemaPaths: ['assets']
          },
          {
            label: MenuAttributes.Liabilities,
            link: ['Liabilities'],
            schemaPaths: ['liabilities', 'monthlyExpenses']
          },
          {
            label: MenuAttributes.Purpose,
            link: ['Purposes'],
            schemaPaths: ['purposes']
          }
        ],
        [
          {
            label: MenuAttributes.BorrowingCapacity,
            link: ['BorrowingCapacity']
          },
          {
            label: MenuAttributes.FundsPosition,
            link: ['FundsPosition']
          },
          {
            label: MenuAttributes.ProductSelection,
            link: ['ProductSelection']
          }
        ],
        [
          {
            label: MenuAttributes.MyProtectionPlan,
            link: ['MyProtectionPlan']
          }
        ],
        [
          {
            label: MenuAttributes.Documents,
            link: ['Documents']
          },
          {
            label: MenuAttributes.LoanTracker,
            link: ['LoanTracker']
          }
        ]
      ],
      conditionalSections: [
        [
          {
            label: MenuAttributes.Notes,
            link: ['Notes']
          },
          {
            label: MenuAttributes.Tasks,
            link: ['Tasks']
          },
          {
            label: MenuAttributes.Refixes,
            link: ['Refixes']
          },
          {
            label: MenuAttributes.Events,
            link: ['Events']
          },
          {
            label: MenuAttributes.Audit,
            link: ['Audit']
          }
        ]
      ],
      showSubmitButton: false
    }
  },
  text: {
    [TextAttributes.OpportunityNameLabel]: 'Opportunity name',
    [TextAttributes.CopyDetailsLabel]:
      'Copy details from an existing opportunity',
    [TextAttributes.SearchOpportunitiesPlaceholder]:
      'search for an opportunity',
    [TextAttributes.OpportunityHeader]: 'Opportunity details',
    [TextAttributes.EditOpportunity]: 'Edit Opportunity',
    [TextAttributes.OpportunityNameValidation]: 'Opportunity name is required',
    [TextAttributes.OpportunitySelectionRequired]:
      'Please select an opportunity',
    [TextAttributes.SubmissionModalHeaderSubmitting]: 'Submitting Opportunity',
    [TextAttributes.SubmissionModalHeaderSubmitted]: 'Submitted Opportunity',
    [TextAttributes.SubmissionInProgress]: '',
    [TextAttributes.SubmissionComplete]: '',
    [TextAttributes.SubmissionModalButton]: '',
    [TextAttributes.SubmissionStatusTimeOut]: '',
    [TextAttributes.SubmissionProductRecommendationRequired]:
      'A product recommendation is required prior to submitting the opportunity',
    [TextAttributes.ProductSearchPlaceholder]: 'search by lender or product',
    [TextAttributes.SubmitOpportunityTitle]: 'Submit Opportunity'
  },
  product: {
    searchResultDetails: [
      ProductSearchResultAttributes.ProductName,
      ProductSearchResultAttributes.Rate,
      ProductSearchResultAttributes.RateType,
      ProductSearchResultAttributes.Term,
      ProductSearchResultAttributes.MaxLVR,
      ProductSearchResultAttributes.SLA,
      ProductSearchResultAttributes.LoyaltyNPS
    ],
    productType: ProductType.Both,
    excludedFeatures: [],
    showProductSelectionMinimum: true,
    showRecommended: true,
    filterByProductGroup: false,
    disableProductsByMaxLoanAmount: false,
    skipAmountAndShowPackageSummary: false,
    PRODUCT_SELECTION_MAXIMUM: 5,
    limitSingleProductSelection: false,
    details: [
      ProductDetails.Lender,
      ProductDetails.Product,
      ProductDetails.Amount,
      ProductDetails.LoanTerm
    ],
    readOnlyFees: [
    ]
  },
  submission: {
    showSubmissionModal: false,
    emitSubmissionEvent: false
  }
};
